import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Dropdown, Form, Alert, Button } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { subQuarters } from "date-fns";
import { debounce } from "lodash";

import { saveAs } from "file-saver";
import {
  getLunaUrl,
  getClientUrl,
  getNumericalHeader,
  chartJSGraphDownload,
  formatCategoryString,
  axios,
  processNextpageUrl,
} from "../utils";

import DomainPanel from "../../ReusableComponents/DomainPanel";
import MyTooltip from "../../ReusableComponents/MyTooltip";
import "../../staticfiles/css/email-volume.css";
import HarmonicDiscountChart from "./HarmonicDiscountChart";
import HarmonicDiscountTable from "./HarmonicDiscountTable";
import { getEarliestAllowedDate } from "./TableUtils";
import ExportRequestsPanel from "./ExportRequestsPanel";


const CampaignDiscount = ({
  uniqueDomains,
  tickerCate,
  loaded,
  lastSundayFI,
}) => {
  const [uniqueTickers, setUniqueTickers] = useState([]);
  const [selectedTicker, setSelectedTicker] = useState("");

  const [selectedTickerDomains, setSelectedTickerDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState({});

  const [allDomainCategories, setAllDomainCategories] = useState({});
  const [domainCategories, setDomainCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [minStartDate, setminStartDate] = useState("");
  const [maxStartDate, setmaxStartDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [tempStartDate, setTempStartDate] = useState("");
  const [harmonicStartDate, setHarmonicStartDate] = useState("");
  const [harmonicStartDateValid, setHarmonicStartDateValid] = useState("");
  const [harmonicEndDate, setHarmonicEndDate] = useState("");
  const [harmonicEndDateValid, setHarmonicEndDateValid] = useState("");

  const [minEndDate, setminEndDate] = useState("");
  const [maxEndDate, setmaxEndDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");

  const [dataFetched, setDataFetched] = useState(false);
  const [initialFetched, setInitialFetched] = useState(false);
  const [discountChartFetching, setDiscountChartFetching] = useState(false);
  const [discountTableFetching, setDiscountTableFetching] = useState(false);
  const [discountTableRefresh, setDiscountTableRefresh] = useState(false);
  const [discountChartData, setDiscountChartData] = useState([]);
  const [discountTableData, setDiscountTableData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");

  const [displayUSData, setDisplayUSData] = useState(false);
  const [yoyToggle, setYoyToggle] = useState(false);
  const [legendCache, setLegendCache] = useState(null);
  const [showLeftAxis, setShowLeftAxis] = useState(true);

  const discountChartRef = useRef(null);
  const domainRef = useRef("");
  const cateRef = useRef("");

  const addYoYData = (data) => {
    const dataMap = new Map(data.map(item => [item.end_date, item]));

    const calculateYoYPercent = (currentValue, yoyValue) => {
      if (yoyValue !== null && yoyValue !== undefined && yoyValue !== 0) {
        return ((currentValue - yoyValue) / yoyValue) * 100;
      }
      return null;
    };

    return data.map((current) => {
      const currentEndDate = moment(current.end_date);
      const yoyDate = currentEndDate.subtract(52, 'weeks').format('YYYY-MM-DD');
      const yoyData = dataMap.get(yoyDate);

      return {
        ...current,
        effective_discount_yoy: calculateYoYPercent(current.effective_discount, yoyData?.effective_discount),
        max_discount_yoy: calculateYoYPercent(current.max_discount, yoyData?.max_discount),
        adjusted_harmonic_is_storewide_total_volume_yoy: calculateYoYPercent(current.adjusted_harmonic_is_storewide_total_volume, yoyData?.adjusted_harmonic_is_storewide_total_volume),
        adjusted_harmonic_is_significant_total_volume_yoy: calculateYoYPercent(current.adjusted_harmonic_is_significant_total_volume, yoyData?.adjusted_harmonic_is_significant_total_volume),
        adjusted_harmonic_is_steep_total_volume_yoy: calculateYoYPercent(current.adjusted_harmonic_is_steep_total_volume, yoyData?.adjusted_harmonic_is_steep_total_volume),
        adjusted_total_volume_yoy: calculateYoYPercent(current.adjusted_total_volume, yoyData?.adjusted_total_volume),
        adjusted_badwords_total_volume_yoy: calculateYoYPercent(current.adjusted_badwords_total_volume, yoyData?.adjusted_badwords_total_volume),
      };
    });
  };

  const handleYoyToggleChange = (checked) => {
    setYoyToggle(checked);
  };

  const getDomainData = async (domain, category) => {
    if (domain === undefined || domain === null) {
      return;
    }
    if (category === "") {
      return;
    }
    setDiscountChartFetching(true);
    setDiscountTableData([]);
    await axios
      .get(
        getLunaUrl(
          `${displayUSData
            ? "v1/emailfeed/weekly-volume/us/batch/"
            : "v1/emailfeed/weekly-volume/batch/"
          }?domain=${domain}&category=${category}`
        ),
        getNumericalHeader()
      )
      .then((response) => {
        let curDomain = response.data[0].domain;
        let curCate = response.data[0].category;
        if (curDomain !== domainRef.current || curCate !== cateRef.current) {
          return;
        } else if (response.data.length === 0) {
          toast.warn(
            "Promotional Ensemble currently don't have information for this domain and category combination"
          );
          setDiscountChartData([]);
        } else {
          const dataWithYoY = addYoYData(response.data);
          let tempminStartDate = new Date(response.data[0].start_date);
          let tempminStartDateIndex = 0;

          let tempmaxStartDate = new Date(response.data[0].start_date);
          let tempmaxStartDateIndex = 0;

          let tempminEndDate = new Date(response.data[0].end_date);
          let tempminEndDateIndex = 0;

          let tempmaxEndDate = new Date(response.data[0].end_date);
          let tempmaxEndDateIndex = 0;

          for (let i = 0; i < response.data.length; i++) {
            let newDate = new Date(response.data[i].start_date);
            if (tempminStartDate > newDate) {
              tempminStartDate = newDate;
              tempminStartDateIndex = i;
            }
            if (tempmaxStartDate < newDate) {
              tempmaxStartDate = newDate;
              tempmaxStartDateIndex = i;
            }

            let newEnd = new Date(response.data[i].end_date);
            if (tempminEndDate > newEnd) {
              tempminEndDate = newEnd;
              tempminEndDateIndex = i;
            }
            if (tempmaxEndDate < newEnd) {
              tempmaxEndDate = newEnd;
              tempmaxEndDateIndex = i;
            }
          }

          let newStartDate = new Date(
            response.data[tempmaxEndDateIndex].end_date
          );
          const c = subQuarters(newStartDate, 5);

          let month = "" + (c.getMonth() + 1);
          let day = "" + c.getDate();
          let year = c.getFullYear();

          if (month.length < 2) month = "0" + month;
          if (day.length < 2) day = "0" + day;

          const formattedNewStartDate = [year, month, day].join("-");

          setminStartDate(response.data[tempminStartDateIndex].start_date);
          setmaxStartDate(response.data[tempmaxStartDateIndex].start_date);

          setStartDate(formattedNewStartDate);
          setTempStartDate(formattedNewStartDate);

          setminEndDate(response.data[tempminEndDateIndex].end_date);
          setmaxEndDate(response.data[tempmaxEndDateIndex].end_date);

          setEndDate(response.data[tempmaxEndDateIndex].end_date);
          setTempEndDate(response.data[tempmaxEndDateIndex].end_date);

          setDiscountChartData(dataWithYoY);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setDiscountChartFetching(false);
        setDataFetched(true);
      });
  };

  const debouncedGetDomainData = debounce(getDomainData, 1500);

  const getNextDiscountTable = async (url, list) => {
    await axios
      .get(url, getNumericalHeader())
      .then(async (response) => {
        let results = response.data.results;
        for (let i = 0; i < results.length; i++) {
          let cate = formatCategoryString(results[i].email_category);
          let entry = {
            subject: results[i].email_subject,
            category: cate,
            end_date: results[i].email_end_date,
            total_volume: results[i].project_total_volume,
            min_discount_pct: results[i].min_discount_pct,
            max_discount_pct: results[i].max_discount_pct,
            effective_discount_pct: results[i].effective_discount_pct,
            state_discount: results[i].state_discount,
            product_type: results[i].product_type,
            is_storewide: results[i].is_storewide,
            is_steep: results[i].is_steep,
            is_significant: results[i].is_significant,
            is_clearance: results[i].is_clearance,
            holiday_tag: results[i].holiday_tag,
            free_ship_condition: results[i].free_ship_condition,
            feed_id: results[i].feed_id,
            rule_discount_id: results[i].rule_discount_id,
            last_update: results[i].last_update,
            has_rule: results[i].rule_discount_id === null ? false : true,
            is_significant_nlp: results[i].nlp_is_significant,
            is_steep_nlp: results[i].nlp_is_steep,
            is_storewide_nlp: results[i].nlp_is_storewide,
            max_discount_pct_nlp: results[i].nlp_max_discount_pct,
            min_discount_pct_nlp: results[i].nlp_min_discount_pct,
            product_type_nlp: results[i].nlp_product_type,
            state_discount_nlp: results[i].nlp_state_discount,
            effective_discount_pct_nlp: results[i].nlp_effective_discount_pct,
            free_ship_condition_nlp: results[i].nlp_free_ship_condition,
            holiday_tag_nlp: results[i].nlp_holiday_tag,
          };
          if (entry.has_rule) {
            entry["min_discount_pct_rule"] =
              results[i].sys_min_discount_pct_rule;
            entry["max_discount_pct_rule"] =
              results[i].sys_max_discount_pct_rule;
            entry["effective_discount_pct_rule"] =
              results[i].sys_effective_discount_pct_rule;
            entry["state_discount_rule"] = results[i].sys_state_discount_rule;
            entry["product_type_rule"] = results[i].sys_product_type_rule;
            entry["is_storewide_rule"] = results[i].sys_is_storewide_rule;
            entry["is_steep_rule"] = results[i].sys_is_steep_rule;
            entry["is_significant_rule"] = results[i].sys_is_significant_rule;
            entry["holiday_tag_rule"] = results[i].sys_holiday_tag_rule;
            entry["free_ship_condition_rule"] =
              results[i].sys_free_ship_condition_rule;
          }
          list.push(entry);
        }

        if (response.data.next !== null) {
          await getNextDiscountTable(
            processNextpageUrl(response.data.next),
            list
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getAllDiscountData = async (num, startDate, endDate, db_year, list) => {
    let requests = [];
    for (let i = 2; i <= num; ++i) {
      let tempRequest = axios.get(
        getLunaUrl(
          `v1/harmonic-discount/cached/list/?domain=${selectedDomain.domain}&startDate=${startDate}&endDate=${endDate}&category=${selectedCategory}&db_year=${db_year}&page=${i}`
        ),
        getNumericalHeader()
      );
      requests.push(tempRequest);
    }

    await axios
      .all(requests)
      .then(
        axios.spread((...responses) => {
          for (let r of responses) {
            let results = r.data.results;
            for (let i = 0; i < results.length; i++) {
              let cate = results[i].email_category
                .split(",")
                .filter((c) => c !== "");
              if (cate.length === 0) {
                cate = "";
              } else if (cate.length === 1) {
                cate = cate[0];
              } else {
                let temp = "";
                for (let s of cate) {
                  temp += s + ", ";
                }
                cate = temp.slice(0, -2);
              }
              let entry = {
                cached_harmonic_id: results[i].cached_harmonic_id,
                subject: results[i].email_subject,
                category: cate,
                end_date: results[i].email_end_date,
                state_discount_val: results[i].state_discount_val,
                total_volume: results[i].project_total_volume,
                min_discount_pct: results[i].min_discount_pct,
                max_discount_pct: results[i].max_discount_pct,
                effective_discount_pct: results[i].effective_discount_pct,
                state_discount: results[i].state_discount,
                product_type: results[i].product_type,
                is_storewide: results[i].is_storewide,
                is_steep: results[i].is_steep,
                is_significant: results[i].is_significant,
                is_clearance: results[i].is_clearance,
                holiday_tag: results[i].holiday_tag,
                free_ship_condition: results[i].free_ship_condition,
                email_image: results[i].email_image_url,
                feed_id: results[i].feed_id,
                rule_discount_id: results[i].rule_discount_id,
                last_update: results[i].last_update,
                has_rule: results[i].rule_discount_id === null ? false : true,
                is_significant_nlp: results[i].nlp_is_significant,
                is_steep_nlp: results[i].nlp_is_steep,
                is_storewide_nlp: results[i].nlp_is_storewide,
                max_discount_pct_nlp: results[i].nlp_max_discount_pct,
                min_discount_pct_nlp: results[i].nlp_min_discount_pct,
                product_type_nlp: results[i].nlp_product_type,
                state_discount_nlp: results[i].nlp_state_discount,
                effective_discount_pct_nlp:
                  results[i].nlp_effective_discount_pct,
                free_ship_condition_nlp: results[i].nlp_free_ship_condition,
                holiday_tag_nlp: results[i].nlp_holiday_tag,
              };
              if (entry.has_rule) {
                entry["min_discount_pct_rule"] =
                  results[i].sys_min_discount_pct_rule;
                entry["max_discount_pct_rule"] =
                  results[i].sys_max_discount_pct_rule;
                entry["effective_discount_pct_rule"] =
                  results[i].sys_effective_discount_pct_rule;
                entry["state_discount_rule"] =
                  results[i].sys_state_discount_rule;
                entry["product_type_rule"] = results[i].sys_product_type_rule;
                entry["is_storewide_rule"] = results[i].sys_is_storewide_rule;
                entry["is_steep_rule"] = results[i].sys_is_steep_rule;
                entry["is_significant_rule"] =
                  results[i].sys_is_significant_rule;
                entry["holiday_tag_rule"] = results[i].sys_holiday_tag_rule;
                entry["free_ship_condition_rule"] =
                  results[i].sys_free_ship_condition_rule;
              }
              list.push(entry);
            }
          }
        })
      )
      .catch((errors) => {
        console.log(errors);
      });
  };

  const getDiscountTableData = async () => {
    let discountList = [];
    let end_date;
    let start_date;
    let tempStartDate, tempEndDate;

    if (selectedDate !== "") {
      end_date = moment(selectedDate);
      start_date = moment(selectedDate);
      start_date.subtract(6, "d");
      tempStartDate = start_date.format("YYYY-MM-DD");
      tempEndDate = end_date.format("YYYY-MM-DD");

      if (new Date(tempStartDate) < new Date(minStartDate)) {
        toast.warn("The selected date range is before the earliest available date: " + minStartDate);
        return;
      }

      if (new Date(tempEndDate) > new Date(maxEndDate)) {
        toast.warn("The selected date range is after the latest available date: " + maxEndDate);
        return;
      }
      setHarmonicStartDate(tempStartDate);
      setHarmonicEndDate(tempEndDate);
    } else {
      if (!validateHarmonicDate()) {
        return;
      }
      start_date = moment(harmonicStartDate);
      end_date = moment(harmonicEndDate);
    }
    let start_year = start_date.year();
    let end_year = end_date.year();
    start_date = start_date.format("YYYY-MM-DD");
    end_date = end_date.format("YYYY-MM-DD");
    setDiscountTableFetching(true);
    let queryUrl = `v1/harmonic-discount/cacheweekly?domain=${selectedDomain.domain}&startDate=${start_date}&endDate=${end_date}&category=${selectedCategory}`;
    await axios
      .get(getClientUrl(queryUrl), getNumericalHeader())
      .then(async (response) => {
        let results = response.data.results;
        for (let i = 0; i < results.length; i++) {
          let cate = results[i].email_category
            .split(",")
            .filter((c) => c !== "");
          if (cate.length === 0) {
            cate = "";
          } else if (cate.length === 1) {
            cate = cate[0];
          } else {
            let temp = "";
            for (let s of cate) {
              temp += s + ", ";
            }
            cate = temp.slice(0, -2);
          }
          let entry = {
            cached_harmonic_id: results[i].cached_harmonic_id,
            subject: results[i].email_subject,
            category: cate,
            start_date: results[i].email_start_date,
            end_date: results[i].email_end_date,
            total_volume: results[i].project_total_volume,
            min_discount_pct: results[i].min_discount_pct,
            max_discount_pct: results[i].max_discount_pct,
            effective_discount_pct: results[i].effective_discount_pct,
            state_discount: results[i].state_discount,
            state_discount_val: results[i].state_discount_val,
            product_type: results[i].product_type,
            is_storewide: results[i].is_storewide,
            is_steep: results[i].is_steep,
            is_significant: results[i].is_significant,
            is_clearance: results[i].is_clearance,
            holiday_tag: results[i].holiday_tag,
            free_ship_condition: results[i].free_ship_condition,
            email_image: results[i].email_image_url,
            feed_id: results[i].feed_id,
            nlp_discount_id: results[i].nlp_discount_id,
            nlp_editor: results[i].nlp_editor,
            nlp_badwords: results[i].nlp_badwords,
            rule_discount_id: results[i].rule_discount_id,
            last_update: results[i].last_update,
            has_rule: results[i].rule_discount_id === null ? false : true,
            is_significant_nlp: results[i].nlp_is_significant,
            is_steep_nlp: results[i].nlp_is_steep,
            is_storewide_nlp: results[i].nlp_is_storewide,
            max_discount_pct_nlp: results[i].nlp_max_discount_pct,
            min_discount_pct_nlp: results[i].nlp_min_discount_pct,
            product_type_nlp: results[i].nlp_product_type,
            state_discount_nlp: results[i].nlp_state_discount,
            effective_discount_pct_nlp: results[i].nlp_effective_discount_pct,
            free_ship_condition_nlp: results[i].nlp_free_ship_condition,
            holiday_tag_nlp: results[i].nlp_holiday_tag,
          };
          if (entry.has_rule) {
            entry["min_discount_pct_rule"] =
              results[i].sys_min_discount_pct_rule;
            entry["max_discount_pct_rule"] =
              results[i].sys_max_discount_pct_rule;
            entry["effective_discount_pct_rule"] =
              results[i].sys_effective_discount_pct_rule;
            entry["state_discount_rule"] = results[i].sys_state_discount_rule;
            entry["product_type_rule"] = results[i].sys_product_type_rule;
            entry["is_storewide_rule"] = results[i].sys_is_storewide_rule;
            entry["is_steep_rule"] = results[i].sys_is_steep_rule;
            entry["is_significant_rule"] = results[i].sys_is_significant_rule;
            entry["holiday_tag_rule"] = results[i].sys_holiday_tag_rule;
            entry["free_ship_condition_rule"] =
              results[i].sys_free_ship_condition_rule;
          }
          discountList.push(entry);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    setHarmonicStartDateValid(start_date);
    setHarmonicEndDateValid(end_date);

    setDiscountTableData(discountList);
    setSelectedDate("");
    setDiscountTableFetching(false);
  };

  const getAllHistData = async (
    start_date,
    end_date,
    db_year,
    discountList,
  ) => {
    let queryUrl = `v1/harmonic-discount/cached/list/?domain=${selectedDomain.domain}&startDate=${start_date}&endDate=${end_date}&category=${selectedCategory}&db_year=${db_year}`;

    await axios
      .get(getLunaUrl(queryUrl), getNumericalHeader())
      .then(async (response) => {
        let results = response.data.results;
        for (let i = 0; i < results.length; i++) {
          let cate = results[i].email_category
            .split(",")
            .filter((c) => c !== "");
          if (cate.length === 0) {
            cate = "";
          } else if (cate.length === 1) {
            cate = cate[0];
          } else {
            let temp = "";
            for (let s of cate) {
              temp += s + ", ";
            }
            cate = temp.slice(0, -2);
          }
          let entry = {
            cached_harmonic_id: results[i].cached_harmonic_id,
            subject: results[i].email_subject,
            category: cate,
            start_date: results[i].email_start_date,
            end_date: results[i].email_end_date,
            total_volume: results[i].project_total_volume,
            min_discount_pct: results[i].min_discount_pct,
            max_discount_pct: results[i].max_discount_pct,
            effective_discount_pct: results[i].effective_discount_pct,
            state_discount: results[i].state_discount,
            state_discount_val: results[i].state_discount_val,
            product_type: results[i].product_type,
            is_storewide: results[i].is_storewide,
            is_steep: results[i].is_steep,
            is_significant: results[i].is_significant,
            is_clearance: results[i].is_clearance,
            holiday_tag: results[i].holiday_tag,
            free_ship_condition: results[i].free_ship_condition,
            email_image: results[i].email_image_url,
            feed_id: results[i].feed_id,
            nlp_discount_id: results[i].nlp_discount_id,
            nlp_editor: results[i].nlp_editor,
            nlp_badwords: results[i].nlp_badwords,
            rule_discount_id: results[i].rule_discount_id,
            last_update: results[i].last_update,
            has_rule: results[i].rule_discount_id === null ? false : true,
            is_significant_nlp: results[i].nlp_is_significant,
            is_steep_nlp: results[i].nlp_is_steep,
            is_storewide_nlp: results[i].nlp_is_storewide,
            max_discount_pct_nlp: results[i].nlp_max_discount_pct,
            min_discount_pct_nlp: results[i].nlp_min_discount_pct,
            product_type_nlp: results[i].nlp_product_type,
            state_discount_nlp: results[i].nlp_state_discount,
            effective_discount_pct_nlp: results[i].nlp_effective_discount_pct,
            free_ship_condition_nlp: results[i].nlp_free_ship_condition,
            holiday_tag_nlp: results[i].nlp_holiday_tag,
          };
          if (entry.has_rule) {
            entry["min_discount_pct_rule"] =
              results[i].sys_min_discount_pct_rule;
            entry["max_discount_pct_rule"] =
              results[i].sys_max_discount_pct_rule;
            entry["effective_discount_pct_rule"] =
              results[i].sys_effective_discount_pct_rule;
            entry["state_discount_rule"] = results[i].sys_state_discount_rule;
            entry["product_type_rule"] = results[i].sys_product_type_rule;
            entry["is_storewide_rule"] = results[i].sys_is_storewide_rule;
            entry["is_steep_rule"] = results[i].sys_is_steep_rule;
            entry["is_significant_rule"] = results[i].sys_is_significant_rule;
            entry["holiday_tag_rule"] = results[i].sys_holiday_tag_rule;
            entry["free_ship_condition_rule"] =
              results[i].sys_free_ship_condition_rule;
          }
          discountList.push(entry);
        }
        if (response.data.next !== null) {
          let page_num = Math.ceil(response.data.count / 50);
          await getAllDiscountData(
            page_num,
            start_date,
            end_date,
            db_year,
            discountList
          );
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const validateHarmonicDate = () => {
    let startDate = harmonicStartDate;
    let endDate = harmonicEndDate;
    const earliestAllowedDate = getEarliestAllowedDate();
    if (moment(startDate).isBefore(earliestAllowedDate) || moment(endDate).isBefore(earliestAllowedDate)) {
      toast.warn("Only 12 months of data available");
      return false;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warn("you cannot set a start date that is later than the end date");
      return false;
    }

    if ((new Date(endDate) - new Date(startDate)) / 1000 / 60 / 60 / 24 > 30) {
      toast.warn(
        "you cannot set a start date that is 30 days earlier than the end date"
      );
      return false;
    }

    if (new Date(startDate) < new Date(minStartDate)) {
      toast.warn("the earliest date is: " + minStartDate);
      return false;
    }

    if (new Date(endDate) > new Date(maxEndDate)) {
      toast.warn("the latest date is: " + maxEndDate);
      return false;
    }

    return true;
  };

  const changeStartDate = () => {
    let newDate = tempStartDate;

    if (new Date(newDate) > new Date(endDate)) {
      toast.warn("you cannot set a start date that is later than the end date");
      setTempStartDate(startDate);
      return;
    }

    if (new Date(newDate) < new Date(minStartDate)) {
      toast.warn("the earlies start date is: " + minStartDate);
      setTempStartDate(minStartDate);
      setStartDate(minStartDate);
      return;
    }

    if (new Date(newDate) > new Date(maxStartDate)) {
      toast.warn("the latest start date is: " + maxStartDate);
      setTempStartDate(maxStartDate);
      setStartDate(maxStartDate);
      return;
    }

    setStartDate(newDate);
  };

  const changeEndDate = () => {
    let newDate = tempEndDate;

    if (new Date(newDate) < new Date(startDate)) {
      toast.warn(
        "you cannot set an end date that is earlier than the start date"
      );
      setTempEndDate(endDate);
      return;
    }

    if (new Date(newDate) < new Date(minEndDate)) {
      toast.warn("the earlies end date is: " + minEndDate);
      setEndDate(minEndDate);
      setTempEndDate(minEndDate);
      return;
    }

    if (new Date(newDate) > new Date(maxEndDate)) {
      toast.warn("the latest end date is: " + maxEndDate);
      setEndDate(maxEndDate);
      setTempEndDate(maxEndDate);
      return;
    }

    setEndDate(newDate);
  };

  const handleTickerSelection = (newTicker) => {
    let newDomains = uniqueDomains
      .filter((domain) => {
        return (
          domain.ticker === newTicker && domain.parent_domain === domain.domain
        );
      })
      .sort();
    let newDomain = newDomains[0];

    setSelectedTicker(newTicker);
    setSelectedTickerDomains(newDomains);
    setSelectedDomain(newDomain);

    let currentCategories = (
      allDomainCategories.get(newDomain.name) || [""]
    ).sort();
    let currCate = currentCategories.find(
      (c) => c.toLowerCase().startsWith("promo")
    );
    if (!currCate) {
      currCate = currentCategories[0];
    } else {
      // Move currCate to the front if it's not already
      currentCategories.splice(
        0,
        0,
        currentCategories.splice(currentCategories.indexOf(currCate), 1)[0]
      );
    }

    setDomainCategories(currentCategories || [""]);
    setSelectedCategory(currCate);
  };

  const handleDomainSelection = (newDomain) => {
    setSelectedDomain(newDomain);

    let currentCategories = (
      allDomainCategories.get(newDomain.name) || [""]
    ).sort();
    let currCate = currentCategories.filter((c) => c.includes("promotional"));
    if (currCate.length === 0) {
      currCate = currentCategories[0];
    } else {
      currCate = currCate[0];
      currentCategories.splice(
        0,
        0,
        currentCategories.splice(currentCategories.indexOf(currCate), 1)[0]
      );
    }

    setDomainCategories(currentCategories || [""]);
    setSelectedCategory(currCate);
  };

  const renderDownloadBtn = (imgDownload, csvDownload) => {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "10px" }}>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setShowLeftAxis(!showLeftAxis)}
          style={{ marginRight: "10px" }}
        >
          {showLeftAxis ? "Hide Left Axis" : "Show Left Axis"}
        </Button>
        <Dropdown className="cloud-download-btn" style={{ margin: 0, width: "auto" }}>
          <Dropdown.Toggle variant="outline-primary">
            <i className="dripicons-cloud-download"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu align="right">
            <Dropdown.Item onClick={imgDownload}>as Image</Dropdown.Item>
            <Dropdown.Item onClick={csvDownload}>as CSV</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const imgDownload = () => {
    if (discountChartRef !== null) {
      chartJSGraphDownload(
        discountChartRef.current.chartInstance,
        selectedTicker +
        " " +
        selectedDomain.domain +
        " " +
        selectedCategory +
        " Harmonic Discount Chart.png"
      );
    }
  };

  const csvDownload = () => {
    const cols = [
      { data: "start_date", name: "Date" },
      { data: "effective_discount", name: "Effective discount" },
      { data: "max_discount", name: "Max Discount" },
      { data: "adjusted_total_volume", name: "Total Volume" },
      {
        data: "adjusted_harmonic_is_significant_total_volume",
        name: "Significant Discounts",
      },
      {
        data: "adjusted_harmonic_is_steep_total_volume",
        name: "Steep Discounts",
      },
      {
        data: "adjusted_harmonic_is_storewide_total_volume",
        name: "Storewide Discounts",
      },
      { data: "adjusted_badwords_total_volume", name: "Desperation Discounts" },
    ];

    const orderFields = (item, orderArray) => {
      let orderedItem = {};
      orderArray.forEach((fieldName) => {
        if (item.hasOwnProperty(fieldName)) {
          orderedItem[fieldName] = item[fieldName];
        }
      });
      return orderedItem;
    };

    if (discountChartRef !== null) {
      let csvData = discountChartData
        .map((row) =>
          orderFields(
            row,
            cols.map((f) => f.data)
          )
        )
        .map((row) => Object.values(row).join(","))
        .join("\n");
      csvData = cols.map((f) => f.name).join(",") + "\n" + csvData;
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
      saveAs(
        blob,
        selectedTicker +
        " " +
        selectedDomain.domain +
        " " +
        selectedCategory +
        " Harmonic Discount.csv"
      );
    }
  };

  useEffect(() => {
    document.title = "Harmonic Discount";
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const fetchData = () => {
      let res = tickerCate;
      let companies = Object.keys(res).sort();
      let domainCategories = new Map();

      for (let i = 0; i < companies.length; i++) {
        let oneCompany = res[companies[i]];
        let domains = Object.keys(oneCompany);

        for (let j = 0; j < domains.length; j++) {
          if (domainCategories.get(domains[j])) {
            domainCategories.set(domains[j], [
              ...domainCategories.get(domains[j]),
              ...oneCompany[domains[j]],
            ]);
          } else {
            domainCategories.set(domains[j], oneCompany[domains[j]]);
          }
        }
      }
      while (companies[0] === "") {
        companies.splice(0, 1);
      }
      let cacheTicker = sessionStorage.getItem("selectedTicker");
      let cacheDomain = sessionStorage.getItem("selectedDomain");
      let initialDomains = uniqueDomains
        .filter((domain) => {
          return (
            domain.ticker === (cacheTicker ? cacheTicker : companies[0]) &&
            domain.parent_domain === domain.domain
          );
        })
        .sort();
      let initialDomain =
        Object.keys(selectedDomain).length === 0 &&
          cacheDomain &&
          cacheDomain != "{}"
          ? JSON.parse(cacheDomain)
          : initialDomains[0];

      setUniqueTickers(companies);
      if (selectedTicker === "" && cacheTicker && cacheTicker !== "{}") {
        setSelectedTicker(cacheTicker);
      } else {
        setSelectedTicker(companies[0]);
      }
      setSelectedTickerDomains(initialDomains);
      setSelectedDomain(initialDomain);
      setAllDomainCategories(domainCategories);
      setDomainCategories(
        (domainCategories.get(initialDomain?.name) || [""]).sort()
      );
      let cacheCate = sessionStorage.getItem("selectedCategory");
      let initialCategories = (domainCategories.get(initialDomain.name) || [""]).sort();
      let promoCate = initialCategories.find(c => c.toLowerCase().startsWith("promo"));
      setSelectedCategory(
        selectedCategory === "" && cacheCate
          ? cacheCate
          : promoCate || initialCategories[0]
      );
      setInitialFetched(true);
    };
    fetchData();
  }, [loaded]);

  useEffect(() => {
    if (selectedDate === "") return;
    getDiscountTableData();
  }, [selectedDate]);

  useEffect(() => {
    if (!sessionStorage.getItem("selectedTicker") || selectedTicker !== "") {
      sessionStorage.setItem("selectedTicker", selectedTicker);
    }
  }, [selectedTicker]);

  useEffect(() => {
    let newDomain = JSON.stringify(selectedDomain);
    if (!sessionStorage.getItem("selectedDomain") || newDomain !== "{}") {
      sessionStorage.setItem("selectedDomain", newDomain);
    }
    domainRef.current = selectedDomain.domain;
  }, [selectedDomain]);

  useEffect(() => {
    if (
      !sessionStorage.getItem("selectedCategory") ||
      selectedCategory !== ""
    ) {
      sessionStorage.setItem("selectedCategory", selectedCategory);
    }
    cateRef.current = selectedCategory;
  }, [selectedCategory]);

  useEffect(() => {
    if (!selectedDomain?.domain || selectedCategory === "") {
      setDiscountChartData([]);
      return;
    }
    debouncedGetDomainData(selectedDomain.domain, selectedCategory);
  }, [selectedDomain, selectedCategory, displayUSData]);

  useEffect(() => {
    if (!harmonicStartDate && !harmonicEndDate && endDate // Only run if endDate is set
    ) {
      // Only proceed if endDate is a valid date string
      if (!endDate || !moment(endDate, "YYYY-MM-DD", true).isValid()) {
        // Wait until endDate is valid
        return;
      }
      const endDateMoment = moment(endDate, "YYYY-MM-DD");
      const tmpEndDate = endDateMoment.format("YYYY-MM-DD");
      const fourWeeksAgo = endDateMoment.clone().subtract(4, "weeks").format("YYYY-MM-DD");
      setHarmonicEndDate(tmpEndDate);
      setHarmonicStartDate(fourWeeksAgo);
      setHarmonicEndDateValid(tmpEndDate);
      setHarmonicStartDateValid(fourWeeksAgo);
    }
    setDiscountTableRefresh(prev => !prev); // Trigger a refresh of the discount table
  }, [
    discountChartFetching
  ]);
  useEffect(() => {
    if (harmonicStartDate && harmonicEndDate) {
      getDiscountTableData();
    }
    setDiscountTableRefresh(false)
  }, [
    discountTableRefresh]);

  if (!initialFetched) {
    return (
      <div className="loader-wrapper">
        <div className="loader"></div>
      </div>
    );
  }
  return (
    <div>
      <h4 className="page-title">Promotional Ensemble</h4>
      {/* <MyTooltip
				content={
					"chart: v1/emailfeed/weekly-volume/batch/, table: v1/harmonic-discount/cached/weekly/list/"
				}
			></MyTooltip> */}
      <DomainPanel
        selectedTicker={selectedTicker}
        selectedDomain={selectedDomain}
        selectedCategory={selectedCategory}
        selectedTickerDomains={selectedTickerDomains}
        uniqueTickers={uniqueTickers}
        uniqueDomains={uniqueDomains}
        domainCategories={domainCategories}
        handleTickerSelection={handleTickerSelection}
        handleDomainSelection={handleDomainSelection}
        setSelectedCategory={setSelectedCategory}
        tempStartDate={tempStartDate}
        setTempStartDate={setTempStartDate}
        changeStartDate={changeStartDate}
        tempEndDate={tempEndDate}
        setTempEndDate={setTempEndDate}
        changeEndDate={changeEndDate}
        useDataToggle={false}
        displayUSData={displayUSData}
        onDataToggleChange={setDisplayUSData}
        useYoYToggle={true}
        onYoYToggleChange={handleYoyToggleChange}
      />
      <Row>
        <Col sm={12} md={12}>
          <div className="card" style={{ overflow: "scroll" }}>
            <div className="card-title"></div>
            <div className="card-body" style={{ minWidth: "800px" }}>
              <HarmonicDiscountChart
                dataFetched={dataFetched}
                isLoading={discountChartFetching}
                chartData={discountChartData}
                startDate={startDate}
                endDate={endDate}
                graphRef={discountChartRef}
                tickerName={selectedTicker}
                domainName={selectedDomain.name}
                categoryName={selectedCategory}
                setSelectedDate={setSelectedDate}
                yoyToggle={yoyToggle}
                legendCache={legendCache}
                setLegendCache={setLegendCache}
                showLeftAxis={showLeftAxis}
              />
              {renderDownloadBtn(imgDownload, csvDownload)}
            </div>

          </div>
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">
                <Row>
                  Select a time range to see the data or click the chart bar
                </Row>
              </h4>
              <Row>
                <Col md={3}>
                  Start Date (earliest: {getEarliestAllowedDate().format("YYYY-MM-DD")}):{" "}
                  <input
                    name="date"
                    id="harmonicStartDate"
                    value={harmonicStartDate}
                    onChange={(e) => setHarmonicStartDate(e.target.value)}
                    type="date"
                    class="form-control"
                  ></input>
                </Col>
                <Col md={3}>
                  End Date:{" "}
                  <input
                    name="date"
                    id="harmonicEndDate"
                    value={harmonicEndDate}
                    onChange={(e) => setHarmonicEndDate(e.target.value)}
                    type="date"
                    class="form-control"
                  ></input>
                </Col>
                <Col md={2}>
                  <br />
                  <button
                    className="btn btn-primary"
                    onClick={() => getDiscountTableData()}
                  >
                    fetch
                  </button>
                </Col>
              </Row>
            </div>
          </div>
          <ExportRequestsPanel
            domain={selectedDomain?.domain || ""}
            category={selectedCategory || null}
            defaultStartDate={harmonicStartDate}
            defaultEndDate={harmonicEndDate}
          />
          <HarmonicDiscountTable
            dataFetching={discountTableFetching}
            startDate={harmonicStartDateValid}
            endDate={harmonicEndDateValid}
            totalData={discountTableData}
            categories={domainCategories}
            getDiscountTableData={getDiscountTableData}
            lastSundayFI={lastSundayFI}
          />
        </Col>
      </Row>
    </div>
  );
};

export default CampaignDiscount;
